import { EMessageType, type IChatMessage } from '@kognitos/lattice';

import type { V1Event } from '@/generated/hooks/rain/models';
import { captureMessage } from '@/modules/sentry';

import { FOLLOW_UP_ID_PREFIX } from '../constants';
import type {
  IAssistantAccumulator,
  IMessageSink,
  IRainCompactionState,
  IRainFollowUpState,
  IRainInterruptState,
  IRainSleepState,
  IRainToolCallState,
} from '../hooks/types';
import { getEventTaskScope, parseRecordedAtMs } from './events';
import {
  buildAssistantMessage,
  createRandomMessageId,
} from './message-helpers';
import { rainv1ValueToJs } from './value';

// ---------------------------------------------------------------------------
// Pure sink-targeted handlers — no React state, no toast, no Sentry except for
// the contract-drift warning in `applyToolCallStart`, which is an observability
// concern of the handler itself. UI side effects (toast, query invalidation)
// live in the React hook at the dispatch boundary.
// ---------------------------------------------------------------------------

export const appendDeltaIntoSink = (
  accum: Map<string, IAssistantAccumulator>,
  taskScope: string,
  delta: string,
  type: EMessageType,
  sink: IMessageSink,
  // Server event id of the delta that OPENS this stretch. Used as the stable
  // message id so a re-delivered stretch (SSE reconnect resuming from
  // Last-Event-ID) upserts the same bubble in place instead of minting a fresh
  // random id and appending a duplicate — the same idempotency input echoes
  // already get from `event.id`. Falls back to a random id only when the event
  // carries none (e.g. synthetic events in tests), preserving prior behavior.
  openEventId?: string,
  /**
   * `recorded_at_ms` of the delta that OPENS this stretch, kept as the
   * message's timestamp so a settled work block can report its duration. See
   * `IAssistantAccumulator.startedAtMs`.
   */
  recordedAtMs?: number,
): void => {
  const existing = accum.get(taskScope);
  const next: IAssistantAccumulator = existing
    ? {
        id: existing.id,
        text: existing.text + delta,
        startedAtMs: existing.startedAtMs,
      }
    : {
        id: openEventId ?? createRandomMessageId(),
        text: delta,
        startedAtMs: recordedAtMs,
      };
  accum.set(taskScope, next);
  // Signature-only reasoning deltas carry an `opaque` replay blob and no text.
  if (!next.text.trim()) return;
  sink.upsertMessage(buildAssistantMessage(next, type));
};

/**
 * Closes the open assistant text bubble(s) for a task scope at a turn boundary
 * so the next delta starts a fresh message instead of gluing onto the prior one.
 * Same-type deltas keep accumulating; every other event seals the
 * bubble it doesn't belong to. `reminder`/`instruction_text` interleave as
 * hidden context, so they seal nothing.
 */
export const sealAssistantTextOnBoundary = (
  sink: IMessageSink,
  taskScope: string,
  event: V1Event,
): void => {
  if (event.reminder || event.instruction_text) return;
  if (!event.output_text) {
    sink.outputAccum.delete(taskScope);
  }
  if (!event.reasoning_text && !event.reasoning_summary) {
    sink.reasoningAccum.delete(taskScope);
  }
};

export const applyToolCallStart = (
  sink: IMessageSink,
  event: V1Event,
  buildToolCallMessage: (state: IRainToolCallState) => IChatMessage,
  /**
   * Tools the browser resolves itself, from the active agent's profile
   * (`IRainAgentConfig.clientResolvedTools`). Which tools block on the client is
   * a per-agent fact, so the set is passed in rather than enumerated here.
   */
  clientResolvedTools?: ReadonlySet<string>,
): void => {
  const start = event.tool_call_start;
  if (!start?.id || !start.name) return;
  const taskScope = getEventTaskScope(event);
  if (start.index !== undefined) {
    sink.toolCallIndexToId.set(`${taskScope}:${start.index}`, start.id);
  }
  // Sanity check: these tools are resolved client-side, so the server must
  // announce them as remote. A mismatch means the contract drifted — log so we
  // notice, but render.
  const isClientResolvedTool = clientResolvedTools?.has(start.name) ?? false;
  if (isClientResolvedTool && start.is_remote !== true) {
    captureMessage(`Rain tool "${start.name}" arrived without is_remote=true`, {
      level: 'warning',
      tags: { module: 'rain-chat', action: 'tool_call_start' },
      extra: { toolName: start.name, isRemote: start.is_remote },
    });
  }
  const existing = sink.toolCalls.get(start.id);
  const startedAtMs = parseRecordedAtMs(event.recorded_at_ms);
  // Idempotent re-init on reconnect: don't rewind a completed call.
  const state: IRainToolCallState = existing ?? {
    id: start.id,
    name: start.name,
    status: 'running',
    args: {},
    isRemote: start.is_remote,
    statusTemplate: start.status_template,
    startedAtMs,
  };
  sink.toolCalls.set(start.id, state);
  sink.upsertMessage(buildToolCallMessage(state));
};

export const applyToolCallArgument = (
  sink: IMessageSink,
  event: V1Event,
  buildToolCallMessage: (state: IRainToolCallState) => IChatMessage,
): void => {
  const arg = event.tool_call_argument;
  if (!arg || arg.index === undefined || !arg.name) return;
  const taskScope = getEventTaskScope(event);
  const id = sink.toolCallIndexToId.get(`${taskScope}:${arg.index}`);
  if (!id) return;
  const existing = sink.toolCalls.get(id);
  if (!existing) return;
  const decoded = rainv1ValueToJs(arg.value);
  const next: IRainToolCallState = {
    ...existing,
    args: { ...existing.args, [arg.name]: decoded ?? null },
  };
  sink.toolCalls.set(id, next);
  sink.upsertMessage(buildToolCallMessage(next));
};

export const applyToolResultToSink = (
  sink: IMessageSink,
  event: V1Event,
  buildToolCallMessage: (state: IRainToolCallState) => IChatMessage,
): void => {
  const tool = event.tool_result;
  const toolCallId = tool?.tool_call_id;
  if (!tool || !toolCallId) return;
  const existing = sink.toolCalls.get(toolCallId);
  const decoded = rainv1ValueToJs(tool.content);
  const next: IRainToolCallState = {
    // Spread first: a result must not erase what the call already carries —
    // an approval decision and its reason, the start clock, remote-tool
    // bookkeeping. Only the fields below belong to the result itself.
    ...existing,
    id: toolCallId,
    name: tool.name ?? existing?.name ?? '',
    status: tool.success ? 'success' : 'error',
    args: existing?.args ?? {},
    result: decoded,
    errorMessage: tool.error,
    durationMicros: tool.duration_micros,
    statusTemplate: existing?.statusTemplate,
    // The call has run, so nothing is pending on the user any more.
    awaitingApproval: false,
    isSubmitting: false,
  };
  sink.toolCalls.set(toolCallId, next);
  sink.upsertMessage(buildToolCallMessage(next));
};

export const applyInterrupt = (
  sink: IMessageSink,
  event: V1Event,
  buildInterruptMessage: (state: IRainInterruptState) => IChatMessage,
  registerOwner: (interruptId: string) => void,
): void => {
  const interrupt = event.interrupt;
  if (!interrupt?.interrupt_id) return;
  const existing = sink.interrupts.get(interrupt.interrupt_id);
  const state: IRainInterruptState = existing ?? {
    interruptId: interrupt.interrupt_id,
    questions: interrupt.questions ?? [],
    answered: false,
    isSubmitting: false,
  };
  const next: IRainInterruptState = {
    ...state,
    questions: interrupt.questions ?? state.questions,
  };
  sink.interrupts.set(interrupt.interrupt_id, next);
  registerOwner(interrupt.interrupt_id);
  sink.upsertMessage(buildInterruptMessage(next));
};

export const applyInterruptAnswer = (
  sink: IMessageSink,
  event: V1Event,
  buildInterruptMessage: (state: IRainInterruptState) => IChatMessage,
): void => {
  const answer = event.interrupt_answer;
  if (!answer?.interrupt_id) return;
  const existing = sink.interrupts.get(answer.interrupt_id);
  if (!existing) return;
  const next: IRainInterruptState = {
    ...existing,
    answered: true,
    answers: answer.answers ?? [],
    isSubmitting: false,
  };
  sink.interrupts.set(answer.interrupt_id, next);
  // Freeze the question card in place — it already re-hydrates and highlights
  // the picked option(s), so this is the sole record of the answer (no
  // separate echo message).
  sink.upsertMessage(buildInterruptMessage(next));
};

/**
 * Freezes the sink's unanswered interrupt cards in `interruptIds` as skipped.
 * Mirrors Rain's empty-answer close-out on redirect without waiting for its `interrupt_answer` echo (a card left interactive would 412).
 */
export const closeUnansweredInterruptsForSink = (
  sink: IMessageSink,
  buildInterruptMessage: (state: IRainInterruptState) => IChatMessage,
  interruptIds: ReadonlySet<string>,
): void => {
  for (const [interruptId, state] of sink.interrupts) {
    if (!interruptIds.has(interruptId)) continue;
    if (state.answered || state.isSubmitting) continue;
    const next: IRainInterruptState = { ...state, answered: true, answers: [] };
    sink.interrupts.set(interruptId, next);
    sink.upsertMessage(buildInterruptMessage(next));
  }
};

export const applyCompactStart = (
  sink: IMessageSink,
  event: V1Event,
  buildCompactionMessage: (
    id: string,
    state: IRainCompactionState,
  ) => IChatMessage,
): void => {
  const start = event.compact_start;
  if (!start?.compact_id) return;
  const state: IRainCompactionState = { status: 'running' };
  sink.compactions.set(start.compact_id, state);
  sink.upsertMessage(buildCompactionMessage(start.compact_id, state));
};

export const applyCompactComplete = (
  sink: IMessageSink,
  event: V1Event,
  buildCompactionMessage: (
    id: string,
    state: IRainCompactionState,
  ) => IChatMessage,
): void => {
  const complete = event.compact_complete;
  if (!complete?.compact_id) return;
  const state: IRainCompactionState = {
    status: 'complete',
    content: complete.content,
  };
  sink.compactions.set(complete.compact_id, state);
  sink.upsertMessage(buildCompactionMessage(complete.compact_id, state));
};

export const applyCompactFailed = (
  sink: IMessageSink,
  event: V1Event,
  buildCompactionMessage: (
    id: string,
    state: IRainCompactionState,
  ) => IChatMessage,
): void => {
  const failure = event.compact_failed;
  if (!failure?.compact_id) return;
  const state: IRainCompactionState = {
    status: 'failed',
    errorMessage: failure.message,
  };
  sink.compactions.set(failure.compact_id, state);
  sink.upsertMessage(buildCompactionMessage(failure.compact_id, state));
};

export const applySleepRequested = (
  sink: IMessageSink,
  event: V1Event,
  buildSleepMessage: (id: string, state: IRainSleepState) => IChatMessage,
): void => {
  const sleep = event.sleep_requested;
  if (!sleep?.sleep_id) return;
  // Derive the start time from the event's wall-clock `requested_at` (Unix
  // seconds) so the countdown survives a page reload / historical replay.
  // Falling back to Date.now() would re-stamp the start time on every replay,
  // restarting the timer from the beginning.
  const requestedAtMs = sleep.requested_at
    ? Number(sleep.requested_at) * 1000
    : Date.now();
  const state: IRainSleepState = {
    status: 'sleeping',
    durationSeconds: sleep.seconds,
    reason: sleep.reason,
    startedAt: Number.isFinite(requestedAtMs) ? requestedAtMs : Date.now(),
  };
  sink.sleeps.set(sleep.sleep_id, state);
  sink.upsertMessage(buildSleepMessage(sleep.sleep_id, state));
};

export const applySleepCompleted = (
  sink: IMessageSink,
  event: V1Event,
  buildSleepMessage: (id: string, state: IRainSleepState) => IChatMessage,
): void => {
  const sleep = event.sleep_completed;
  if (!sleep?.sleep_id) return;
  const existing = sink.sleeps.get(sleep.sleep_id);
  const next: IRainSleepState = {
    ...(existing ?? { status: 'completed' }),
    status: 'completed',
    sleptForSeconds: sleep.slept_for_seconds,
    interruptedByUser: sleep.interrupted_by_user,
  };
  sink.sleeps.set(sleep.sleep_id, next);
  sink.upsertMessage(buildSleepMessage(sleep.sleep_id, next));
};

const warnMissingToolCall = (action: string, toolCallId: string): void => {
  captureMessage(`Rain ${action} arrived without a known tool call`, {
    level: 'warning',
    tags: { module: 'rain-chat', action },
    extra: { toolCallId },
  });
};

export const applyToolApprovalRequested = (
  sink: IMessageSink,
  event: V1Event,
  buildToolCallMessage: (state: IRainToolCallState) => IChatMessage,
): void => {
  const req = event.tool_approval_requested;
  const toolCallId = req?.tool_call_id;
  if (!toolCallId) return;
  const existing = sink.toolCalls.get(toolCallId);
  if (!existing) {
    warnMissingToolCall('tool_approval_requested', toolCallId);
    return;
  }
  const next: IRainToolCallState = { ...existing, awaitingApproval: true };
  sink.toolCalls.set(toolCallId, next);
  sink.upsertMessage(buildToolCallMessage(next));
};

export const applyToolApprovalGranted = (
  sink: IMessageSink,
  event: V1Event,
  buildToolCallMessage: (state: IRainToolCallState) => IChatMessage,
): void => {
  const granted = event.tool_approval_granted;
  const toolCallId = granted?.tool_call_id;
  if (!toolCallId) return;
  const existing = sink.toolCalls.get(toolCallId);
  if (!existing) {
    warnMissingToolCall('tool_approval_granted', toolCallId);
    return;
  }
  const next: IRainToolCallState = {
    ...existing,
    awaitingApproval: false,
    approvalDecision: 'granted',
    isSubmitting: false,
  };
  sink.toolCalls.set(toolCallId, next);
  sink.upsertMessage(buildToolCallMessage(next));
};

export const applyToolApprovalDenied = (
  sink: IMessageSink,
  event: V1Event,
  buildToolCallMessage: (state: IRainToolCallState) => IChatMessage,
): void => {
  const denied = event.tool_approval_denied;
  const toolCallId = denied?.tool_call_id;
  if (!toolCallId) return;
  const existing = sink.toolCalls.get(toolCallId);
  if (!existing) {
    warnMissingToolCall('tool_approval_denied', toolCallId);
    return;
  }
  const next: IRainToolCallState = {
    ...existing,
    awaitingApproval: false,
    approvalDecision: 'denied',
    denialReason: denied?.reason,
    isSubmitting: false,
  };
  sink.toolCalls.set(toolCallId, next);
  sink.upsertMessage(buildToolCallMessage(next));
};

export const applyFollowUp = (
  sink: IMessageSink,
  event: V1Event,
  buildFollowUpMessage: (state: IRainFollowUpState) => IChatMessage,
): void => {
  const suggestion = event.follow_up?.suggestion;
  if (!suggestion) return;

  // Coalesce successive follow_up events from the same turn into one card by
  // tracking the open follow-up id on the sink. The id resets on the next
  // user-initiated send (see useRainThreadMessages).
  const openId = sink.currentFollowUpIdRef.current;
  if (openId && sink.followUps.has(openId)) {
    const existing = sink.followUps.get(openId);
    if (!existing) return;
    if (existing.suggestions.includes(suggestion)) return;
    const next: IRainFollowUpState = {
      ...existing,
      suggestions: [...existing.suggestions, suggestion],
    };
    sink.followUps.set(openId, next);
    sink.upsertMessage(buildFollowUpMessage(next));
    return;
  }

  const followUpId = createRandomMessageId();
  const state: IRainFollowUpState = {
    followUpId,
    suggestions: [suggestion],
    isSubmitting: false,
  };
  sink.followUps.set(followUpId, state);
  sink.currentFollowUpIdRef.current = followUpId;
  sink.upsertMessage(buildFollowUpMessage(state));
};

/**
 * Closes (marks answered + removes) the given sink's currently-open
 * follow-up card, if any. Shared by `closeOpenFollowUps` in
 * useRainThreadMessages (the explicit user-action call sites — send a
 * message, reply to an interrupt, approve/deny a tool call, which loop this
 * across root + sub-agent sinks) and `routeEventThroughSink`'s own
 * event-driven check, which catches the case those user actions can't: the
 * agent continuing on its own after a completed turn (the generation
 * reducer's "reactivate" case in generation-state.ts) with no user action to
 * hook into at all.
 */
export const closeFollowUpForSink = (sink: IMessageSink): void => {
  const openId = sink.currentFollowUpIdRef.current;
  if (openId) {
    const existing = sink.followUps.get(openId);
    if (existing && !existing.isAnswered) {
      sink.followUps.set(openId, {
        ...existing,
        isAnswered: true,
        isSubmitting: false,
      });
      sink.removeMessage(`${FOLLOW_UP_ID_PREFIX}${openId}`);
    }
  }
  sink.currentFollowUpIdRef.current = null;
};
